import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { VehicleType } from '@polaris-customerweb/sharedcomponents.core';
import {
    CartesianTile,
    GeoBounds,
} from '@polaris-customerweb/sharedcomponents.shared';
import { find, includes } from 'lodash-es';
import { combineLatest, NEVER, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
    SetRideConditionsInView,
    UpdateRideConditionTileCache,
} from 'src/app/components/ride-condition/ride-condition.actions';
import {
    selectRideConditionsInView,
    selectRideConditionTileCaches,
} from 'src/app/components/ride-condition/ride-condition.reducer';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { RideCondition } from 'src/app/models/ride-condition';
import { RideConditionTileCache } from 'src/app/models/ride-condition-cache';
import { GeoBoundsTileService } from './geo-bounds-tile.service';
import { RideConditionResource } from './ride-condition-resource.service';

@Injectable({ providedIn: 'root' })
export class RideConditionService {
    rideConditions: RideCondition[];

    constructor(
        private geoBoundsTileService: GeoBoundsTileService,
        private rideConditionResource: RideConditionResource,
        private store: Store<AppState>
    ) {
        store
            .pipe(select(selectRideConditionsInView))
            .subscribe(
                (rideConditions) => (this.rideConditions = rideConditions)
            );
    }

    //#region Get
    getRideConditionsByLocation$(
        lat: number,
        lng: number,
        dist: number,
        rideType: string,
        lastModified?: string,
        reportedAfter?: string
    ): Observable<RideCondition[]> {
        return this.rideConditionResource
            .getRideConditionsByLocation(
                lat,
                lng,
                dist,
                rideType,
                lastModified,
                reportedAfter
            )
            .pipe(
                tap((conditions) => {
                    this.store.dispatch(
                        new SetRideConditionsInView(conditions)
                    );
                })
            );
    }

    getRideConditions(
        boundingBox: GeoBounds,
        vehicleType: VehicleType,
        maxRideConditionAgeInHours: number,
        lastModified?: string
    ): Observable<RideCondition[]> {
        const reportedAfter = new Date();
        reportedAfter.setHours(
            reportedAfter.getHours() - maxRideConditionAgeInHours
        );

        return this.getRideConditionTilesToUpdate(
            boundingBox,
            maxRideConditionAgeInHours
        ).pipe(
            switchMap((tiles) => {
                const boundsToRequest = tiles.map((tile) => tile.geoBounds);

                if (boundsToRequest.length === 0) {
                    return NEVER;
                }

                return combineLatest([
                    this.rideConditionResource.getRideConditionsByMultipleLocation(
                        boundsToRequest,
                        vehicleType,
                        reportedAfter.toISOString(),
                        lastModified
                    ),
                    of(tiles),
                ]);
            }),
            map(([conditions, tiles]) => {
                // update the tiles cache and return conditions
                const timestamp = Date.now();
                const tileCache = tiles.map((tile) => {
                    return new RideConditionTileCache(
                        tile,
                        timestamp,
                        maxRideConditionAgeInHours
                    );
                });
                this.store.dispatch(
                    new UpdateRideConditionTileCache(tileCache)
                );

                return conditions;
            })
        );
    }

    getRideCondition(id: string): Observable<RideCondition | undefined> {
        return this.store
            .select(selectRideConditionsInView)
            .pipe(
                map((rideConditions) =>
                    rideConditions.find(
                        (rideCondition) => rideCondition.id === id
                    )
                )
            );
    }

    getRideConditionTilesToUpdate(
        bounds: GeoBounds,
        maxRideConditionAgeInHours: number
    ): Observable<CartesianTile[]> {
        return this.store.pipe(
            select(selectRideConditionTileCaches),
            take(1),
            map((rideConditionTileCaches) => {
                const allTiles = this.geoBoundsTileService.getTiles(bounds);
                const cachedTileIds = rideConditionTileCaches.reduce<string[]>(
                    (cacheIds, cache) => {
                        if (
                            !cache.isExpired &&
                            cache.maxRideConditionAgeInHours ===
                                maxRideConditionAgeInHours
                        ) {
                            cacheIds.push(cache.tileId);
                        }

                        return cacheIds;
                    },
                    []
                );

                // remove tiles that have unexpired caches that match max age
                return allTiles.filter(
                    (tile) => !includes(cachedTileIds, tile.id)
                );
            })
        );
    }

    getCachedRideCondition(id: string): RideCondition | undefined {
        return find(this.rideConditions, { id });
    }
    //#endregion Get

    //#region Update
    reportAbuse(rideCondition: RideCondition): Observable<void> {
        return this.rideConditionResource.reportAbuse(rideCondition);
    }
    //#endregion

    rideConditionsToGeoJson(
        rideConditions: RideCondition[] = []
    ): GeoJSON.FeatureCollection {
        const features = rideConditions.map((condition) =>
            condition.toGeoJson()
        );
        return new GeoJsonFeatureCollection(features);
    }
}
